// @flow
import * as React from 'react';
import ReactDOM from 'react-dom';
import { Button, Box } from 'grommet/es6';
import { Close } from 'grommet-icons/es6';
import { baseStyle } from 'grommet/utils';
import styled, { css } from 'styled-components';

import ModalActions from './ModalActions';
import ModalContent from './ModalContent';
import ModalHeader from './ModalHeader';

type Props = {
    children: React.Node,
    onClose: (e: SyntheticEvent<any>) => void,
    closeOnOutsideClick: boolean,
    showCloseBtn: boolean,
    size: 'small' | 'medium' | 'large' | 'full_width',
    position: 'left' | 'right',
}

const ModalCover = styled(Box)`
  ${baseStyle}
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1101;
  transform: translateZ(0);
  background-color: rgba(0,0,0, 0.5);
  overflow-y: auto;
  pointer-events: none;
  ${(props) => props.position && `align-items: ${props.position === 'left' ? 'flex-start' : 'flex-end'};`}
`;

const sizes = {
    small: '30%',
    medium: '50%',
    large: '80%',
    full_width: '100%',
};

const ModalDialog = styled.div`
  position: fixed;
  top: 0;
  left: 50%;
  height: auto;
  width: ${(props) => sizes[props.size]};
  padding: 1%;
  background-color: #FFFFFF;
  box-shadow: 0 0 10px 3px rgba(0, 0, 0, 0.1);
  -webkit-overflow-scrolling: touch;
  transform: translate(-50%, 5%);
  max-width: ${(props) => sizes[props.size]};
  pointer-events: initial;
   ${(props) => props.position && css`
                left: auto;
                min-height: 100%;
                transform: none;  
            `}
`;

class Modal extends React.Component<Props> {
    static Header = ModalHeader;

    static Actions = ModalActions;

    static Content = ModalContent;

    render() {
        const {
            children,
            onClose,
            closeOnOutsideClick,
            size,
            position,
            showCloseBtn,
        } = this.props;

        const closeBtn = <Button key="close-modal" icon={<Close />} onClick={onClose} hoverIndicator />;

        const modalChildren = () => {
            if (showCloseBtn) {
                let hasHeader = false; // By default wrap header and close button together
                let toRender = React.Children.map(children, (child, i) => {
                    if (child.type.displayName === 'ModalHeader') {
                        hasHeader = true;
                        // eslint-disable-next-line
                        const childElement = React.cloneElement(child, { key: i });
                        return (
                            React.createElement(
                                Box, { direction: 'row', pad: 'small' }, [childElement, closeBtn],
                            )
                        );
                    }
                    return child;
                });

                if (!hasHeader) { // If there is no header add the close button before the content
                    toRender = (
                        <Box>
                            <Box align="end">{closeBtn}</Box>
                            {children}
                        </Box>
                    );
                }
                return toRender;
            }

            return children;
        };

        const modal = (
            <>
                <div
                    role="none"
                    onClick={closeOnOutsideClick ? onClose : undefined}
                    style={{
                        position: 'fixed',
                        width: '100%',
                        height: '100%',
                        left: 0,
                        top: 0,
                        zIndex: 1000,
                    }}
                />
                <ModalCover className="modal-cover" position={position}>
                    <ModalDialog size={size} pad="small" position={position}>
                        {modalChildren()}
                    </ModalDialog>
                </ModalCover>
            </>
        );

        return ReactDOM.createPortal(modal, document.body);
    }
}

Modal.defaultProps = {
    closeOnOutsideClick: true,
    size: 'medium',
    showCloseBtn: true,
};


export default Modal;
